import { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/Payments.css';

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState('templates');
  const [showAddCategory, setShowAddCategory] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    category: '',
    content: '',
    variables: '',
    active: true
  });

  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    color: '#3b82f6'
  });

  useEffect(() => {
    fetchCategories();
    fetchTemplates();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(Array.isArray(response.data) ? response.data : []);
      if (form.category === '' && response.data.length > 0) {
        setForm(prev => ({ ...prev, category: response.data[0].name }));
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates');
      setTemplates(Array.isArray(response.data) ? response.data : []);
      setLoading(false);
    } catch (err) {
      setError('Failed to load templates');
      setLoading(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name.trim()) {
      setError('Category name required');
      return;
    }
    try {
      await api.post('/categories', newCategory);
      setSuccess('Category added successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setNewCategory({ name: '', description: '', color: '#3b82f6' });
      setShowAddCategory(false);
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add category');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      if (!form.name || !form.category || !form.content) {
        setError('Name, category, and content required');
        setSubmitting(false);
        return;
      }

      const payload = {
        name: form.name,
        category: form.category,
        content: form.content,
        variables: form.variables || null,
        active: form.active
      };

      if (editingId) {
        await api.patch(`/templates/${editingId}`, payload);
        setSuccess('Template updated successfully!');
      } else {
        await api.post('/templates', payload);
        setSuccess('Template created successfully!');
      }

      setTimeout(() => setSuccess(''), 3000);
      setForm({ name: '', category: categories[0]?.name || '', content: '', variables: '', active: true });
      setEditingId(null);
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (template) => {
    setForm({
      name: template.name,
      category: template.category,
      content: template.content,
      variables: template.variables || '',
      active: template.active
    });
    setEditingId(template.id);
    setActiveTab('templates');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/templates/${id}`);
      setSuccess('Template deleted!');
      setTimeout(() => setSuccess(''), 3000);
      fetchTemplates();
    } catch (err) {
      setError('Failed to delete template');
    }
  };

  const handleReset = () => {
    setForm({ name: '', category: categories[0]?.name || '', content: '', variables: '', active: true });
    setEditingId(null);
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2>📋 Message Templates</h2>

      {error && <div style={{ color: '#dc2626', marginBottom: '15px', padding: '12px', background: '#fee2e2', borderRadius: '6px' }}>❌ {error}</div>}
      {success && <div style={{ color: '#16a34a', marginBottom: '15px', padding: '12px', background: '#d1fae5', borderRadius: '6px' }}>✅ {success}</div>}

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #e5e7eb', flexWrap: 'wrap' }}>
        <button 
          style={{ 
            padding: '10px 15px', 
            background: activeTab === 'templates' ? '#2563eb' : '#f3f4f6',
            color: activeTab === 'templates' ? 'white' : '#1f2937',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
          onClick={() => setActiveTab('templates')}
        >
          📝 Templates
        </button>
        <button 
          style={{ 
            padding: '10px 15px', 
            background: activeTab === 'categories' ? '#2563eb' : '#f3f4f6',
            color: activeTab === 'categories' ? 'white' : '#1f2937',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
          onClick={() => setActiveTab('categories')}
        >
          🏷️ Categories
        </button>
      </div>

      {activeTab === 'templates' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
          <div style={{ background: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
            <h3>{editingId ? '✏️ Edit Template' : '➕ Create New Template'}</h3>
            <form onSubmit={handleSubmit} style={{ marginTop: '15px' }}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Template Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Payment Reminder"
                  required
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontFamily: 'inherit' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Category *</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    style={{ flex: 1, padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontFamily: 'inherit' }}
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowAddCategory(!showAddCategory)}
                    style={{ padding: '8px 12px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
                  >
                    ➕ New
                  </button>
                </div>
              </div>

              {showAddCategory && (
                <div style={{ marginBottom: '15px', padding: '15px', background: '#eff6ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                  <h4>Add New Category</h4>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Category Name *</label>
                    <input
                      type="text"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      placeholder="e.g., Support Ticket"
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontFamily: 'inherit' }}
                    />
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Description</label>
                    <input
                      type="text"
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                      placeholder="Optional description"
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontFamily: 'inherit' }}
                    />
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Color</label>
                    <input
                      type="color"
                      value={newCategory.color}
                      onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                      style={{ width: '60px', height: '36px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddCategory}
                    style={{ padding: '8px 15px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
                  >
                    Save Category
                  </button>
                </div>
              )}

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Message Content *</label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  placeholder="Enter your message here..."
                  required
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontFamily: 'inherit', minHeight: '120px', resize: 'vertical' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Variables (comma-separated)</label>
                <input
                  type="text"
                  value={form.variables}
                  onChange={(e) => setForm({ ...form, variables: e.target.value })}
                  placeholder="e.g., {amount}, {due_date}"
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontFamily: 'inherit' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '500' }}>
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    style={{ cursor: 'pointer' }}
                  />
                  Active
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" disabled={submitting} style={{ padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', flex: 1 }}>
                  {submitting ? 'Saving...' : (editingId ? '💾 Update' : '➕ Create')}
                </button>
                {editingId && (
                  <button type="button" onClick={handleReset} style={{ padding: '10px 20px', background: '#f3f4f6', color: '#1f2937', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {!loading && templates.length > 0 && (
            <div style={{ background: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #e5e7eb', overflowX: 'auto' }}>
              <h3>Template Library</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '15px', fontSize: '14px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', background: '#f9fafb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Name</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Category</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Content</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map(template => (
                    <tr key={template.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px' }}>{template.name}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ padding: '4px 8px', background: '#eff6ff', color: '#0c2d6b', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>
                          {template.category}
                        </span>
                      </td>
                      <td style={{ padding: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{template.content}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {template.active ? '✅' : '❌'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <button onClick={() => handleEdit(template)} style={{ marginRight: '8px', padding: '6px 12px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}>
                          Edit
                        </button>
                        <button onClick={() => handleDelete(template.id)} style={{ padding: '6px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'categories' && (
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <h3>Message Categories</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px', marginTop: '15px' }}>
            {categories.map(cat => (
              <div key={cat.id} style={{ padding: '15px', borderRadius: '6px', border: `2px solid ${cat.color}`, background: `${cat.color}15` }}>
                <div style={{ fontSize: '16px', fontWeight: '600', color: cat.color }}>{cat.name}</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{cat.id} categories</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
